import { Injectable, inject } from '@angular/core';
import { firstValueFrom, from, Observable, of } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@environment';

import { H5PParams, H5PSemantics } from './walker';
import { getH5pEditorNamespace } from './h5p-editor-globals';

// Matches an H5P uber name as stored in params ("H5P.MultiChoice 1.14").
const UBER_NAME_PATTERN = /^H5P\.[\w.]+ \d+\.\d+$/;
const LIBRARY_KEY = 'library';
const LOAD_TIMEOUT_MS = 10000;
// The h5p-service endpoint the editor's own `loadLibrary` calls. Used directly
// when the editor globals are absent (see `load`).
const LIBRARIES_AJAX_PATH = '/ajax?action=libraries';

/**
 * Supplies library semantics to the translation walker
 * (`resolveSemantics`). Reads what the live editor already holds in
 * `H5PEditor.libraryCache` and falls back to the editor's own
 * `loadLibrary` ajax (which also feeds that cache), so unopened embedded
 * libraries (lazy VerticalTabs question tabs) resolve too. Results are cached
 * per uber name for the session.
 */
@Injectable({
  providedIn: 'root',
})
export class H5pSemanticsProviderService {
  private readonly cache = new Map<string, H5PSemantics>();

  private readonly api = inject(ApiService);

  /**
   * Collect every embedded-library uber name present in the params tree
   * (deep scan for `library: "H5P.X y.z"` entries).
   */
  collectLibraryNames(params: H5PParams): string[] {
    const found = new Set<string>();
    this.scan(params, found);
    return Array.from(found);
  }

  /**
   * Ensure semantics for the given uber names (plus what the scan of `params`
   * finds) are loaded, and emit a record usable as the walker's
   * `resolveSemantics`.
   */
  prefetch(
    rootLibrary: string,
    params: H5PParams,
  ): Observable<Record<string, H5PSemantics>> {
    const names = new Set<string>(this.collectLibraryNames(params));
    if (UBER_NAME_PATTERN.test(rootLibrary)) {
      names.add(rootLibrary);
    }
    return this.prefetchNames(Array.from(names));
  }

  /**
   * Ensure semantics for an explicit set of uber names are loaded. Callers
   * that count MANY contents at once (the content page's module rows) collect
   * every library across every module first and prefetch them in one go —
   * each missing library is then fetched exactly once, never once per module.
   */
  prefetchNames(
    uberNames: readonly string[],
  ): Observable<Record<string, H5PSemantics>> {
    const names = new Set<string>(
      uberNames.filter(name => UBER_NAME_PATTERN.test(name)),
    );
    const missing = Array.from(names).filter(name => !this.resolveSync(name));
    if (!missing.length) {
      return of(this.snapshot(names));
    }
    return from(
      Promise.all(missing.map(name => this.load(name))).then(() =>
        this.snapshot(names),
      ),
    );
  }

  /** Synchronously resolve semantics from the session/live editor cache. */
  resolveSync(uberName: string): H5PSemantics | undefined {
    const cached = this.cache.get(uberName);
    if (cached) {
      return cached;
    }
    const live = getH5pEditorNamespace()?.libraryCache?.[uberName]?.semantics;
    if (live && Array.isArray(live)) {
      this.cache.set(uberName, live);
      return live;
    }
    return undefined;
  }

  private snapshot(names: Iterable<string>): Record<string, H5PSemantics> {
    const record: Record<string, H5PSemantics> = {};
    for (const name of names) {
      const semantics = this.resolveSync(name);
      if (semantics) {
        record[name] = semantics;
      }
    }
    return record;
  }

  private load(uberName: string): Promise<H5PSemantics | undefined> {
    const loadLibrary = getH5pEditorNamespace()?.loadLibrary;
    if (!loadLibrary) {
      // The H5P editor globals only exist once a module editor has been
      // booted. Surfaces that count WITHOUT opening the editor (the content
      // page's module rows) must still resolve semantics, so fall back to the
      // very endpoint the editor's own loadLibrary calls.
      return this.fetchSemantics(uberName);
    }
    return new Promise(resolve => {
      // The editor's loadLibrary has no error callback — never hang a save on
      // a failed library fetch.
      const guard = setTimeout(() => resolve(undefined), LOAD_TIMEOUT_MS);
      try {
        loadLibrary(uberName, semantics => {
          clearTimeout(guard);
          if (semantics && Array.isArray(semantics)) {
            this.cache.set(uberName, semantics);
          }
          resolve(semantics);
        });
      } catch {
        clearTimeout(guard);
        resolve(undefined);
      }
    });
  }

  /**
   * Editor-independent semantics fetch (`GET /ajax?action=libraries&…`), the
   * same call the editor's `loadLibrary` makes. Never rejects — a missing
   * library simply means the caller falls back to source / renders no tag.
   */
  private async fetchSemantics(
    uberName: string,
  ): Promise<H5PSemantics | undefined> {
    const [machineName, version] = uberName.split(' ');
    const [majorVersion, minorVersion] = (version ?? '').split('.');
    if (!machineName || !majorVersion || !minorVersion) {
      return undefined;
    }
    try {
      const response = await firstValueFrom(
        this.api.get(
          environment.h5pBaseUrl,
          `${LIBRARIES_AJAX_PATH}&machineName=${encodeURIComponent(
            machineName,
          )}&majorVersion=${majorVersion}&minorVersion=${minorVersion}`,
        ),
      );
      const semantics = (response?.body as { semantics?: H5PSemantics })
        ?.semantics;
      if (Array.isArray(semantics)) {
        this.cache.set(uberName, semantics);
        return semantics;
      }
    } catch {
      // Counting is informational — never break the host page.
    }
    return undefined;
  }

  private scan(value: unknown, found: Set<string>): void {
    if (Array.isArray(value)) {
      value.forEach(entry => this.scan(entry, found));
      return;
    }
    if (typeof value !== 'object' || value === null) {
      return;
    }
    const record = value as Record<string, unknown>;
    const library = record[LIBRARY_KEY];
    if (typeof library === 'string' && UBER_NAME_PATTERN.test(library)) {
      found.add(library);
    }
    Object.values(record).forEach(entry => this.scan(entry, found));
  }
}
